package br.gov.go.pge.permanenciaweb.site

import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.theme.colors.palette.MutablePalette
import com.varabyte.kobweb.silk.theme.colors.palette.background
import com.varabyte.kobweb.silk.theme.colors.palette.border
import com.varabyte.kobweb.silk.theme.colors.palette.checkbox
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.focusOutline
import com.varabyte.kobweb.silk.theme.colors.palette.overlay

/**
 * The "Industrial Dense" palette, ported 1:1 from the old static UI's root CSS variables.
 *
 * These colors are the single source of truth for the widget `CssStyle`s and are also registered as
 * first-class entries in the Silk theme palettes below, so Silk widgets can consume them too.
 */
object IndustrialPalette {
    val Surface = Color.rgb(0xFAFAFA)
    val Ink = Color.rgb(0x262626)
    val Muted = Color.rgb(0x6B6B6B)
    val Steel = Color.rgb(0x3B6E91)
    val SteelTint = Color.rgb(0xE8F0F5)
    val Orange = Color.rgb(0xC8742A)
    val OrangeTint = Color.rgb(0xFBEEE1)
    val Red = Color.rgb(0xD13438)
    /** Expired permit chip: neutral dark gray, not an error. */
    val ExpiredBg = Color.rgb(0x4F4F4F)
    val ExpiredFg = Color.rgb(0xF2F2F2)
    val RedTint = Color.rgb(0xFBE4E5)
    val Border = Color.rgb(0xE0E0E0)
    val Grid = Color.rgb(0xE5E5E5)
    val White = Color.rgb(0xFFFFFF)
    val RowHover = Color.rgb(0xF5F8FA)
}

private fun MutablePalette.registerIndustrialPalette() {
    // Standard Silk palette entries. These back the `--silk-*` CSS variables, so Silk widgets
    // (Surface, Overlay, buttons, inputs, ...) pick up the Industrial Dense colors too.
    background = IndustrialPalette.Surface
    color = IndustrialPalette.Ink
    border = IndustrialPalette.Border
    focusOutline = IndustrialPalette.Steel
    // Matches the browser's default `dialog::backdrop` look (~10% black) that the old UI got for free.
    overlay = Colors.Black.copyf(alpha = 0.1f)

    // Silk defaults checkboxes to blue; Industrial Dense uses Steel with a white check.
    checkbox.set(
        background = IndustrialPalette.Steel,
        hover = IndustrialPalette.Steel,
        color = IndustrialPalette.White,
    )
}

@InitSilk
fun initIndustrialTheme(ctx: InitSilkContext) {
    // The site is light-only (as the old UI was); register the palette for both modes anyway so
    // nothing breaks if a Silk widget ever queries the dark palette.
    ctx.theme.palettes.light.registerIndustrialPalette()
    ctx.theme.palettes.dark.registerIndustrialPalette()
}
